// src/components/Map/TruckHistoryViewer.js
// User-friendly garbage truck history viewer
// Helps users see if the truck passed their house today

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API, APP_CONSTANTS } from '../../utils/config';
import MapController from './MapController';
import { vehicleTrackerService } from '../../services/vehicleTrackerService';
import { useAuth } from '../../contexts/AuthContext';

// Component to handle map clicks for setting home location
const MapClickHandler = ({ enabled, onMapClick }) => {
  useMapEvents({
    click: (e) => {
      console.log('[MapClickHandler] Map clicked:', {
        enabled,
        hasOnMapClick: !!onMapClick,
        latlng: e.latlng,
        originalEvent: e.originalEvent?.target?.tagName
      });
      if (enabled && onMapClick) {
        console.log('[MapClickHandler] Calling onMapClick with coordinates:', e.latlng);
        onMapClick(e);
      } else {
        console.log('[MapClickHandler] Click ignored - enabled:', enabled, 'hasCallback:', !!onMapClick);
      }
    },
  });
  return null;
};

// Create home marker icon
const createHomeIcon = () => {
  return L.divIcon({
    html: `<div style="
      font-size: 28px;
      text-align: center;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    ">🏠</div>`,
    className: 'home-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Create truck position marker
const createTruckPositionIcon = (isNearest = false) => {
  return L.divIcon({
    html: `<div style="
      font-size: ${isNearest ? '32px' : '20px'};
      text-align: center;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
      ${isNearest ? 'animation: pulse 1s infinite;' : ''}
    ">${isNearest ? '🚛' : '📍'}</div>
    ${isNearest ? `<style>
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.2); }
      }
    </style>` : ''}`,
    className: 'truck-position-icon',
    iconSize: [isNearest ? 36 : 24, isNearest ? 36 : 24],
    iconAnchor: [isNearest ? 18 : 12, isNearest ? 18 : 12],
  });
};

// Calculate distance between two points in meters
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Convert meters to feet
const metersToFeet = (meters) => Math.round(meters * 3.28084);

// Convert meters to miles
const metersToMiles = (meters) => (meters / 1609.34).toFixed(2);

// Format time for display
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z');
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const TruckHistoryViewer = ({ vehicleId, vehicleName, onClose, checkAllTrucks = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);  // True until we've checked for saved home location
  const [fetchComplete, setFetchComplete] = useState(false);  // True once we've received API response
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [homeLocation, setHomeLocation] = useState(null);
  const [showSetHome, setShowSetHome] = useState(false);
  const [nearestPoint, setNearestPoint] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [mapCenter, setMapCenter] = useState(APP_CONSTANTS.MAP.BELIZE_CENTER);
  // For "all trucks" mode - stores info about which truck is closest
  const [closestTruck, setClosestTruck] = useState(null);
  const [allTrucksResults, setAllTrucksResults] = useState([]);
  // Missed pickup report state
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportResult, setReportResult] = useState(null);

  // Ref for debouncing recheck after map click
  const recheckTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [recheckCountdown, setRecheckCountdown] = useState(0);

  // Log when showSetHome changes
  useEffect(() => {
    console.log('[TruckHistoryViewer] showSetHome changed to:', showSetHome);
  }, [showSetHome]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (recheckTimeoutRef.current) {
        clearTimeout(recheckTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load saved home location - try database first (if logged in), then localStorage
  useEffect(() => {
    const loadHomeLocation = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Try to load from database if logged in
      if (token) {
        try {
          const response = await vehicleTrackerService.getHomeLocation();
          if (response.home_location) {
            setHomeLocation(response.home_location);
            // Also update localStorage as backup
            localStorage.setItem('myHomeLocation', JSON.stringify(response.home_location));
            setInitializing(false);
            return;
          }
        } catch (e) {
          console.log('Could not load home location from database, using localStorage');
        }
      }

      // Fall back to localStorage
      const saved = localStorage.getItem('myHomeLocation');
      if (saved) {
        try {
          setHomeLocation(JSON.parse(saved));
        } catch (e) {
          console.error('Error loading home location:', e);
        }
      }
      setInitializing(false);
    };

    loadHomeLocation();
  }, []);

  // Fetch history for a single truck or all trucks
  const fetchHistory = useCallback(async (abortPrevious = false) => {
    // Only abort previous fetch if explicitly requested (user changed location)
    if (abortPrevious && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this fetch
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);
    setFetchComplete(false);

    // Clear previous results when rechecking
    if (checkAllTrucks) {
      setAllTrucksResults([]);
      setClosestTruck(null);
      setNearestPoint(null);
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      // If checkAllTrucks mode OR no specific vehicleId, use efficient server-side endpoint
      if (checkAllTrucks || !vehicleId) {
        // Load home location
        const savedHome = localStorage.getItem('myHomeLocation');
        const home = savedHome ? JSON.parse(savedHome) : null;

        if (!home) {
          // No home set - can't check nearest trucks
          setLoading(false);
          setFetchComplete(true);
          return;
        }

        // Use efficient server-side endpoint to find nearest trucks
        const response = await fetch(
          `${API}/vehicles/public/nearest-to-location?lat=${home.lat}&lng=${home.lng}&start_date=${startOfDay.toISOString()}&end_date=${endOfDay.toISOString()}`,
          { signal }
        );

        if (!response.ok) throw new Error('Failed to find nearest trucks');

        const data = await response.json();

        // Convert results to the format expected by the UI
        const validTrucks = data.results.map(result => ({
          truck: result.vehicle,
          nearestToHome: {
            latitude: result.closest_point.latitude,
            longitude: result.closest_point.longitude,
            timestamp: result.closest_point.timestamp,
            speed: result.closest_point.speed,
            distance: result.distance_meters,
            distanceFeet: result.distance_feet
          },
          hasData: true,
          totalLocations: result.total_locations_checked
        }));

        setAllTrucksResults(validTrucks);
        setFetchComplete(true);

        // Set the closest truck as the main display
        if (validTrucks.length > 0) {
          const closest = validTrucks[0];
          setClosestTruck(closest.truck);
          setNearestPoint(closest.nearestToHome);

          // Fetch detailed history for the closest truck to show route on map
          try {
            const histResponse = await fetch(
              `${API}/vehicles/${closest.truck.id}/history?start_date=${startOfDay.toISOString()}&end_date=${endOfDay.toISOString()}&limit=2000`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                signal
              }
            );

            if (histResponse.ok) {
              const histData = await histResponse.json();
              setHistoryData(histData);

              // Extract route coordinates
              if (histData.locations?.length > 0) {
                const coords = histData.locations.map(loc => [loc.latitude, loc.longitude]);
                setRouteCoordinates(coords);
                const midIndex = Math.floor(coords.length / 2);
                setMapCenter(coords[midIndex] || coords[0]);
              }
            }
          } catch (e) {
            console.error('Error fetching detailed history:', e);
          }

          // Center map on home if we have it
          if (home) {
            setMapCenter([home.lat, home.lng]);
          }
        }

      } else {
        // Single truck mode (original behavior)
        const response = await fetch(
          `${API}/vehicles/${vehicleId}/history?start_date=${startOfDay.toISOString()}&end_date=${endOfDay.toISOString()}&limit=2000`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load truck history');
        }

        const data = await response.json();
        setHistoryData(data);
        setFetchComplete(true);

        // Extract route coordinates
        if (data.locations && data.locations.length > 0) {
          const coords = data.locations.map(loc => [loc.latitude, loc.longitude]);
          setRouteCoordinates(coords);

          // Center map on the route
          const midIndex = Math.floor(coords.length / 2);
          setMapCenter(coords[midIndex] || coords[0]);
        }
      }

    } catch (err) {
      // Ignore abort errors (user changed location)
      if (err.name === 'AbortError') {
        console.log('[fetchHistory] Request aborted - user changed location');
        return;
      }
      console.error('Error fetching history:', err);
      setError(err.message);
      setFetchComplete(true);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, checkAllTrucks]);

  useEffect(() => {
    if (!initializing) {
      fetchHistory();
    }
  }, [fetchHistory, initializing]);

  // Find nearest point to home when home location or history changes
  // Only for single-truck mode - in checkAllTrucks mode, nearestPoint comes from API
  useEffect(() => {
    // Skip in checkAllTrucks mode - nearestPoint is set directly from API response
    if (checkAllTrucks) {
      return;
    }

    if (!homeLocation || !historyData?.locations?.length) {
      setNearestPoint(null);
      return;
    }

    let nearest = null;
    let minDistance = Infinity;

    historyData.locations.forEach(loc => {
      const distance = calculateDistance(
        homeLocation.lat, homeLocation.lng,
        loc.latitude, loc.longitude
      );
      if (distance < minDistance) {
        minDistance = distance;
        nearest = {
          ...loc,
          distance: distance,
          distanceFeet: metersToFeet(distance)
        };
      }
    });

    setNearestPoint(nearest);
  }, [homeLocation, historyData, checkAllTrucks]);

  // Save home location to database and localStorage
  const saveHomeLocation = async (newHome) => {
    // Always save to localStorage as backup
    localStorage.setItem('myHomeLocation', JSON.stringify(newHome));

    // Try to save to database if logged in
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      try {
        await vehicleTrackerService.updateHomeLocation(newHome.lat, newHome.lng);
        console.log('[saveHomeLocation] Home location saved to database');
      } catch (e) {
        console.log('[saveHomeLocation] Could not save to database, saved to localStorage only');
      }
    }
  };

  // Handle setting home location
  const handleSetHomeFromMap = async (e) => {
    console.log('[handleSetHomeFromMap] Called with event:', e);
    const { lat, lng } = e.latlng;
    console.log('[handleSetHomeFromMap] Setting home to:', { lat, lng });
    const newHome = { lat, lng };
    setHomeLocation(newHome);
    await saveHomeLocation(newHome);
    setShowSetHome(false);
    console.log('[handleSetHomeFromMap] Home location saved successfully');

    // Cancel any existing recheck timeout and abort any in-progress fetch
    if (recheckTimeoutRef.current) {
      clearTimeout(recheckTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLoading(false);

    // Start 5-second countdown before rechecking
    setRecheckCountdown(5);
    countdownIntervalRef.current = setInterval(() => {
      setRecheckCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    recheckTimeoutRef.current = setTimeout(() => {
      clearInterval(countdownIntervalRef.current);
      setRecheckCountdown(0);
      console.log('[handleSetHomeFromMap] Rechecking after 5 second delay');
      fetchHistory(true);  // Abort any previous fetch
    }, 5000);
  };

  // Use current location as home
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newHome = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setHomeLocation(newHome);
          await saveHomeLocation(newHome);
          setShowSetHome(false);
          setMapCenter([newHome.lat, newHome.lng]);

          // Cancel any pending recheck from map click
          if (recheckTimeoutRef.current) {
            clearTimeout(recheckTimeoutRef.current);
          }
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          setRecheckCountdown(0);

          // Immediately recheck with new location
          console.log('[handleUseCurrentLocation] Rechecking immediately');
          fetchHistory(true);  // Abort any previous fetch
        },
        (error) => {
          alert('Could not get your location. Please try clicking on the map instead.');
        }
      );
    }
  };

  // Clear home location
  const handleClearHome = async () => {
    setHomeLocation(null);
    localStorage.removeItem('myHomeLocation');
    setNearestPoint(null);

    // Try to delete from database if logged in
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      try {
        await vehicleTrackerService.deleteHomeLocation();
        console.log('[handleClearHome] Home location deleted from database');
      } catch (e) {
        console.log('[handleClearHome] Could not delete from database');
      }
    }
  };

  // Determine truck proximity status with multiple tiers for accuracy
  // This helps distinguish between your street, parallel streets, and general area
  const getProximityStatus = () => {
    if (!nearestPoint) return 'no_data';

    const distanceMeters = nearestPoint.distance;

    // Very close (< 100 feet / ~30 meters) - Definitely on your street
    if (distanceMeters < 30) {
      return 'definitely_passed';
    }
    // Close (< 200 feet / ~60 meters) - Very likely your street
    if (distanceMeters < 60) {
      return 'likely_passed';
    }
    // Nearby (< 350 feet / ~105 meters) - Could be parallel street
    if (distanceMeters < 105) {
      return 'nearby_uncertain';
    }
    // In the area (< 500 meters / ~1640 feet) - Truck was in general neighborhood
    if (distanceMeters < 500) {
      return 'passed_area_not_street';
    }
    // Somewhat close (< 1500 meters / ~1 mile) - Truck was in the vicinity
    if (distanceMeters < 1500) {
      return 'passed_vicinity';
    }
    // Far (> 1500 meters) - Hasn't been near this area today
    return 'not_passed';
  };

  const proximityStatus = getProximityStatus();

  // Submit missed pickup report
  const handleSubmitReport = async () => {
    setReportSubmitting(true);
    setReportResult(null);

    try {
      const licensePlate = checkAllTrucks
        ? (closestTruck?.license_plate || 'Unknown')
        : (vehicleName || 'Unknown');

      const reportData = {
        user_email: user?.email || null,
        user_phone: user?.phone_number || null,
        home_lat: homeLocation.lat,
        home_lng: homeLocation.lng,
        truck_license_plate: licensePlate,
        truck_nearest_lat: nearestPoint.latitude,
        truck_nearest_lng: nearestPoint.longitude,
        truck_nearest_time: nearestPoint.timestamp || '',
        truck_distance_feet: nearestPoint.distanceFeet,
        proximity_status: proximityStatus,
        message: reportMessage,
      };

      const response = await fetch(`${API}/missed-pickup/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData),
      });

      if (response.ok) {
        setReportResult({ success: true, message: 'Report submitted! We will look into this.' });
        setTimeout(() => {
          setShowReportDialog(false);
          setReportMessage('');
          setReportResult(null);
        }, 3000);
      } else {
        throw new Error('Failed to submit report');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      setReportResult({ success: false, message: 'Failed to submit report. Please try again.' });
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center">
              <span className="mr-2">🚛</span>
              {checkAllTrucks ? "Did a Truck Pass My House?" : "Today's Truck Route"}
            </h2>
            <p className="text-green-100 text-sm mt-1">
              {checkAllTrucks
                ? (closestTruck
                    ? `Closest: ${closestTruck.license_plate} - ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
                    : `Checking all trucks - ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`)
                : `${vehicleName || 'Garbage Truck'} - ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
              }
            </p>
            {checkAllTrucks && allTrucksResults.length > 0 && (
              <p className="text-green-200 text-xs mt-1">
                Checked {allTrucksResults.length} garbage trucks
              </p>
            )}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <span className="text-xl">✕</span>
            </button>
          )}
        </div>
      </div>

      {/* Did it pass my house? Section */}
      <div className="p-4 bg-gray-50 border-b">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center">
          <span className="mr-2">🏠</span>
          Did the truck pass my house?
        </h3>

        {/* Show loading spinner while initializing, loading, or waiting for fetch to complete */}
        {(initializing || (homeLocation && !fetchComplete)) ? (
          <div className="bg-gray-100 rounded-lg p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600 font-medium">Checking all garbage trucks...</span>
            </div>
          </div>
        ) : !homeLocation ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 mb-3">
              Set your home location to check if the garbage truck passed your house today.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleUseCurrentLocation}
                className={`px-4 py-2 rounded-lg transition-all flex items-center ${
                  showSetHome
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={showSetHome}
              >
                <span className="mr-2">📍</span>
                Use My Current Location
              </button>
              <button
                onClick={() => setShowSetHome(!showSetHome)}
                className={`px-4 py-2 rounded-lg transition-all flex items-center font-medium ${
                  showSetHome
                    ? 'bg-orange-500 text-white ring-4 ring-orange-300 animate-pulse shadow-lg'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                <span className="mr-2">{showSetHome ? '🎯' : '👆'}</span>
                {showSetHome ? 'Click Map Below!' : 'Click on Map'}
              </button>
            </div>
            {showSetHome && (
              <div className="mt-3 p-2 bg-orange-100 border-2 border-orange-400 rounded-lg">
                <p className="text-orange-800 text-sm font-medium flex items-center">
                  <span className="text-xl mr-2 animate-bounce">👇</span>
                  Scroll down and tap on the map to place your home
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className={`rounded-lg p-4 ${
            proximityStatus === 'definitely_passed' ? 'bg-green-50 border-2 border-green-400' :
            proximityStatus === 'likely_passed' ? 'bg-green-50 border-2 border-green-300' :
            proximityStatus === 'nearby_uncertain' ? 'bg-yellow-50 border-2 border-yellow-400' :
            proximityStatus === 'passed_area_not_street' ? 'bg-orange-50 border-2 border-orange-300' :
            proximityStatus === 'passed_vicinity' ? 'bg-blue-50 border-2 border-blue-300' :
            'bg-gray-50 border border-gray-300'
          }`}>
            {recheckCountdown > 0 && (
              <div className="flex items-center justify-center py-2 mb-2 bg-blue-100 rounded-lg">
                <div className="animate-pulse text-blue-600 mr-2">⏳</div>
                <span className="text-blue-700 text-sm">
                  Rechecking in {recheckCountdown}s... <span className="text-xs">(tap map to adjust location)</span>
                </span>
              </div>
            )}
            {nearestPoint ? (
              <div>
                {/* Definitely Passed - Very Close */}
                {proximityStatus === 'definitely_passed' && (
                  <div className="flex items-start mb-3">
                    <span className="text-4xl mr-3">✅</span>
                    <div>
                      <p className="text-green-800 font-bold text-lg">Yes! The truck passed your street</p>
                      <p className="text-green-700">
                        Closest at <strong>{formatTime(nearestPoint.timestamp)}</strong>
                      </p>
                      <p className="text-green-600 text-sm mt-1">
                        Only <strong>{nearestPoint.distanceFeet} feet</strong> away - definitely your street
                      </p>
                    </div>
                  </div>
                )}

                {/* Likely Passed - Close */}
                {proximityStatus === 'likely_passed' && (
                  <div className="flex items-start mb-3">
                    <span className="text-4xl mr-3">✅</span>
                    <div>
                      <p className="text-green-800 font-bold text-lg">The truck very likely passed your street</p>
                      <p className="text-green-700">
                        Closest at <strong>{formatTime(nearestPoint.timestamp)}</strong>
                      </p>
                      <p className="text-green-600 text-sm mt-1">
                        <strong>{nearestPoint.distanceFeet} feet</strong> away - very likely your street
                      </p>
                    </div>
                  </div>
                )}

                {/* Nearby but Uncertain - Could be parallel street */}
                {proximityStatus === 'nearby_uncertain' && (
                  <div className="flex items-start mb-3">
                    <span className="text-4xl mr-3">🤔</span>
                    <div>
                      <p className="text-yellow-800 font-bold text-lg">Truck was nearby - check the map</p>
                      <p className="text-yellow-700">
                        Closest at <strong>{formatTime(nearestPoint.timestamp)}</strong>
                      </p>
                      <p className="text-yellow-600 text-sm mt-1">
                        <strong>{nearestPoint.distanceFeet} feet</strong> away - could be a nearby parallel street
                      </p>
                      <p className="text-yellow-600 text-xs mt-1 italic">
                        Check the green route line on the map below to confirm
                      </p>
                    </div>
                  </div>
                )}

                {/* Passed in area but not on street - No predefined route for this street */}
                {proximityStatus === 'passed_area_not_street' && (
                  <div className="flex items-start mb-3">
                    <span className="text-4xl mr-3">🚛</span>
                    <div>
                      <p className="text-orange-800 font-bold text-lg">Truck passed nearby but not on your street</p>
                      <p className="text-orange-700">
                        Closest at <strong>{formatTime(nearestPoint.timestamp)}</strong> — {nearestPoint.distanceFeet} feet away
                      </p>
                      <div className="mt-2 p-2 bg-orange-100 rounded text-sm">
                        <p className="text-orange-700">
                          <strong>Note:</strong> The truck may not have a scheduled route for your street today.
                          Check the map below to see where it went.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Passed in vicinity - within ~1 mile */}
                {proximityStatus === 'passed_vicinity' && (
                  <div className="flex items-start mb-3">
                    <span className="text-4xl mr-3">📍</span>
                    <div>
                      <p className="text-blue-800 font-bold text-lg">Truck was in your general area</p>
                      <p className="text-blue-700">
                        Closest at <strong>{formatTime(nearestPoint.timestamp)}</strong> — {metersToMiles(nearestPoint.distance)} miles away
                      </p>
                      <div className="mt-2 p-2 bg-blue-100 rounded text-sm">
                        <p className="text-blue-700">
                          <strong>No route to your street:</strong> The garbage truck was operating in your general area
                          but didn't come down your street. Your street may not be on today's schedule.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Not Passed Yet - Far away */}
                {proximityStatus === 'not_passed' && (
                  <div className="flex items-start mb-3">
                    <span className="text-4xl mr-3">⏳</span>
                    <div>
                      <p className="text-gray-800 font-bold text-lg">Truck hasn't reached your area yet</p>
                      <p className="text-gray-600">
                        Nearest point was <strong>{metersToMiles(nearestPoint.distance)} miles</strong> away at {formatTime(nearestPoint.timestamp)}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        Check back later or watch the live map
                      </p>
                    </div>
                  </div>
                )}

                {/* Report Missed Pickup Button */}
                {nearestPoint && homeLocation && (
                  <button
                    onClick={() => setShowReportDialog(true)}
                    className="mt-3 w-full px-4 py-2.5 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <span className="mr-2">⚠️</span>
                    Report Missed Pickup
                  </button>
                )}

                {/* Show all trucks that passed nearby in checkAllTrucks mode */}
                {checkAllTrucks && allTrucksResults.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">All trucks checked today:</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {allTrucksResults.map((result, idx) => (
                        <div
                          key={result.truck.id}
                          className={`flex justify-between items-center text-xs p-2 rounded ${
                            idx === 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <span className="font-medium">
                            {idx === 0 && '🏆 '}
                            {result.truck.license_plate}
                          </span>
                          <span>
                            {result.nearestToHome
                              ? result.nearestToHome.distance < 1000
                                ? `${result.nearestToHome.distanceFeet} ft`
                                : `${metersToMiles(result.nearestToHome.distance)} mi`
                              : 'No home set'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center">
                <span className="text-4xl mr-3">📡</span>
                <div>
                  <p className="text-gray-800 font-bold">No data for today yet</p>
                  <p className="text-gray-600">The truck hasn't sent any location updates today.</p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => setShowSetHome(!showSetHome)}
                className={`px-3 py-1.5 text-sm rounded transition-all flex items-center ${
                  showSetHome
                    ? 'bg-orange-500 text-white ring-2 ring-orange-300 animate-pulse font-medium'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <span className="mr-1">{showSetHome ? '🎯' : '📍'}</span>
                {showSetHome ? 'Tap Map Below!' : 'Change Location'}
              </button>
              <button
                onClick={handleClearHome}
                className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Clear
              </button>
            </div>
            {showSetHome && (
              <div className="mt-2 p-2 bg-orange-100 border-2 border-orange-400 rounded-lg">
                <p className="text-orange-800 text-sm font-medium flex items-center">
                  <span className="text-xl mr-2 animate-bounce">👇</span>
                  Scroll down and tap on the map to move your home
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="relative h-80">
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading truck route...</p>
            </div>
          </div>
        )}

        {showSetHome && (
          <div
            className="absolute top-2 left-2 right-2 z-20 bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 rounded-lg shadow-xl border-2 border-orange-300 animate-pulse"
            style={{ pointerEvents: 'none' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-3xl mr-3 animate-bounce">🎯</span>
                <div>
                  <p className="font-bold text-lg">TAP ON THE MAP</p>
                  <p className="text-orange-100 text-sm">Touch anywhere to set your home</p>
                </div>
              </div>
              <button
                onClick={() => setShowSetHome(false)}
                className="px-4 py-2 bg-white text-orange-600 rounded-lg font-medium hover:bg-orange-50 shadow"
                style={{ pointerEvents: 'auto' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <MapContainer
          center={mapCenter}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
        >
          <MapController center={mapCenter} zoom={14} />
          <MapClickHandler enabled={showSetHome} onMapClick={handleSetHomeFromMap} />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* Route polyline */}
          {routeCoordinates.length > 0 && (
            <Polyline
              positions={routeCoordinates}
              color="#059669"
              weight={4}
              opacity={0.8}
            />
          )}

          {/* Home location marker */}
          {homeLocation && (
            <>
              <Marker
                position={[homeLocation.lat, homeLocation.lng]}
                icon={createHomeIcon()}
              >
                <Popup>
                  <div className="text-center">
                    <strong>🏠 Your Home</strong>
                    <p className="text-xs text-gray-600">
                      {homeLocation.lat.toFixed(5)}, {homeLocation.lng.toFixed(5)}
                    </p>
                  </div>
                </Popup>
              </Marker>

              {/* Detection zone circles - from outer to inner */}
              {/* Outer zone: 105m (~350ft) - Uncertain/parallel street zone */}
              <Circle
                center={[homeLocation.lat, homeLocation.lng]}
                radius={105}
                pathOptions={{
                  color: '#f59e0b',
                  fillColor: '#fef3c7',
                  fillOpacity: 0.15,
                  weight: 1,
                  dashArray: '5, 5'
                }}
              />

              {/* Middle zone: 60m (~200ft) - Likely passed zone */}
              <Circle
                center={[homeLocation.lat, homeLocation.lng]}
                radius={60}
                pathOptions={{
                  color: '#22c55e',
                  fillColor: '#bbf7d0',
                  fillOpacity: 0.2,
                  weight: 1,
                  dashArray: '3, 3'
                }}
              />

              {/* Inner zone: 30m (~100ft) - Definitely passed zone */}
              <Circle
                center={[homeLocation.lat, homeLocation.lng]}
                radius={30}
                pathOptions={{
                  color: '#16a34a',
                  fillColor: '#4ade80',
                  fillOpacity: 0.3,
                  weight: 2
                }}
              />

              {/* Outer zone: 500m - Area/neighborhood zone (only shown at lower zoom) */}
              <Circle
                center={[homeLocation.lat, homeLocation.lng]}
                radius={500}
                pathOptions={{
                  color: '#f97316',
                  fillColor: '#ffedd5',
                  fillOpacity: 0.08,
                  weight: 1,
                  dashArray: '10, 5'
                }}
              />
            </>
          )}

          {/* Nearest point marker */}
          {nearestPoint && homeLocation && (
            <Marker
              position={[nearestPoint.latitude, nearestPoint.longitude]}
              icon={createTruckPositionIcon(true)}
            >
              <Popup>
                <div className="text-center">
                  <strong>🚛 Nearest Point</strong>
                  <p className="text-sm text-green-700 font-medium">{formatTime(nearestPoint.timestamp)}</p>
                  <p className="text-xs text-gray-600">{nearestPoint.distanceFeet} feet from your home</p>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="p-3 bg-gray-50 border-t">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-1 bg-green-600 rounded mr-2"></div>
            <span className="text-gray-600">Truck Route</span>
          </div>
          {homeLocation && (
            <div className="flex items-center">
              <span className="mr-1">🏠</span>
              <span className="text-gray-600">Your Home</span>
            </div>
          )}
          {nearestPoint && (
            <div className="flex items-center">
              <span className="mr-1">🚛</span>
              <span className="text-gray-600">Closest Point</span>
            </div>
          )}
        </div>
        {/* Detection zone legend */}
        {homeLocation && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-1 font-medium">Detection Zones:</p>
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-400 border border-green-600 mr-1"></div>
                <span className="text-gray-600">&lt;100ft - Your street</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-200 border border-green-500 border-dashed mr-1"></div>
                <span className="text-gray-600">&lt;200ft - Likely</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-amber-100 border border-amber-500 border-dashed mr-1"></div>
                <span className="text-gray-600">&lt;350ft - Nearby</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-orange-100 border border-orange-400 border-dashed mr-1"></div>
                <span className="text-gray-600">&lt;0.3mi - In area</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-red-50 border-t border-red-200">
          <p className="text-red-700 flex items-center">
            <span className="mr-2">⚠️</span>
            {error}
          </p>
          <button
            onClick={fetchHistory}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Report Missed Pickup Dialog */}
      {showReportDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 20000 }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center">
              <span className="mr-2">⚠️</span>
              Report Missed Pickup
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Let us know that the garbage truck did not collect your garbage.
            </p>

            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-600 space-y-1">
              <p><strong>Truck:</strong> {checkAllTrucks ? (closestTruck?.license_plate || 'Unknown') : (vehicleName || 'Unknown')}</p>
              <p><strong>Closest at:</strong> {formatTime(nearestPoint?.timestamp)} ({nearestPoint?.distanceFeet} ft away)</p>
              {user?.email && <p><strong>Your email:</strong> {user.email}</p>}
            </div>

            <textarea
              value={reportMessage}
              onChange={(e) => setReportMessage(e.target.value)}
              placeholder="Describe what happened (e.g. bin was out since 6 AM but truck drove past without stopping)..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm h-28 resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />

            {reportResult && (
              <div className={`mt-2 p-2 rounded text-sm ${reportResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {reportResult.message}
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setShowReportDialog(false); setReportMessage(''); setReportResult(null); }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                disabled={reportSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center justify-center"
                disabled={reportSubmitting}
              >
                {reportSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Report'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TruckHistoryViewer;
